import { Response } from '@/store/modules/survey-store-module'

export const contractionDictionary: Record<string, number> = {
  aight: 1,
  werent: 1,
  weve: 1,
  were: 1,
  well: 1,
  wed: 1,
  wasnt: 1,
  twas: 1,
  tis: 1,
  thiss: 1,
  theyve: 1,
  theyre: 1,
  theyll: 1,
  theyd: 1,
  theres: 1,
  therere: 1,
  therell: 1,
  thered: 1,
  thatd: 1,
  thats: 1,
  thatre: 1,
  thatll: 1,
  whatd: 1,
  whatll: 1,
  whatre: 1,
  whats: 1,
  youll: 1,
  youd: 1,
  yall: 1,
  wouldnt: 1,
  wouldve: 1,
  wont: 1,
  whys: 1,
  whyre: 1,
  whyd: 1,
  whove: 1,
  youre: 1,
  whos: 1,
  wholl: 1,
  whod: 1,
  whichs: 1,
  whereve: 1,
  wheres: 1,
  wherere: 1,
  wherell: 1,
  whered: 1,
  whens: 1,
  whatve: 1,
  whore: 1,
  someones: 1,
  somethings: 1,
  hows: 1,
  howre: 1,
  howll: 1,
  howd: 1,
  hes: 1,
  hell: 1,
  hed: 1,
  havent: 1,
  hasnt: 1,
  hadnt: 1,
  gotta: 1,
  somebodys: 1,
  gonna: 1,
  everybodys: 1,
  dont: 1,
  doesnt: 1,
  didnt: 1,
  darent: 1,
  couldnt: 1,
  couldve: 1,
  cant: 1,
  arent: 1,
  aint: 1,
  everyones: 1,
  id: 1,
  youve: 1,
  ill: 1,
  shouldnt: 1,
  shouldve: 1,
  shes: 1,
  shell: 1,
  shed: 1,
  shant: 1,
  oughtnt: 1,
  neednt: 1,
  mustve: 1,
  mightve: 1,
  mustnt: 1,
  mightnt: 1,
  maynt: 1,
  lets: 1,
  its: 1,
  itll: 1,
  itd: 1,
  isnt: 1,
  ive: 1,
  im: 1,
  idve: 2,
  mustntve: 2,
  couldntve: 2,
  wedve: 2,
  wouldntve: 2,
  whodve: 2,
  shouldntve: 2
}

export const whiteListWords = [
  'nothing',
  'no idea',
  'no questions',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten'
]

export const customWordReplacements: Record<string, string> = {
  'covid-19': 'covid',
  covid19: 'covid'
}

export const validPosTokens = [
  'n', 'q', /* Nouns */
  'o', 'p', /* Proper Nouns */
  '1', '2', '3', '4', '5', '6', 'l', /* Verbs */
  'h', 'i', 'j', '=']

export const removeApostrophes = (tokens: string[]) => {
  return tokens.map((token: string) => {
    return cleanForLooseMatch(token.replace(/'/g, '')
      .replace(/’/g, ''))
  })
}

/***
 * This will go through a response and line up the tokens 1:1. The main goal is to find where the contractions are and
 * replace them in our parts of speech array with a single * instead of multiple tokens.
 * @param cleanResponse The response we are generating POS tokens for
 * @param tokens The list of tokens WITH the contractions inside
 * @return cleanTokens The list of tokens with *s in place of contractions. This should be 1:1 with cleanResponse word count.
 */
export const removeContractionTokens = (cleanResponse: string[], tokens: string[]) => {
  const cleanTokens: string[] = []
  let offsetCount = 0

  // If they DID use contractions, they didn't use apostrophes. No real need to go through this.
  if (cleanResponse.length === tokens.length) return tokens

  cleanResponse.forEach((word, index) => {
    if (contractionDictionary[word]) {
      offsetCount += contractionDictionary[word]
      cleanTokens.push('*')
    } else {
      cleanTokens.push(cleanForLooseMatch(tokens[index + offsetCount]))
    }
  })

  return cleanTokens
}

/**
 * Used to get a cleaned string for searching and matching. (Is not useful for display as it will strip punctuation)
 * @param term The string that will be cleaned
 * Returns an empty string if term is undefined or false
 */
export const cleanForLooseMatch = (term?: string) => {
  if (!term) return ''
  return term
    .replace(/([/\r?\n|\r/]|[ ]{2,})+/g, ' ') // start by replacing tabs, newlines and multiple spaces with single space
    .replace(/\b[-.,()&$#!\[\]{}"']+\B|\B[-.,()&$#!\[\]{}"']+\b/g, '') // remove punctuation from end of words
    .toLowerCase() // lowercasing the whole answer
    .trim() // and remove any remaining whitespace (maybe not necessary)
}

/**
 * @param wordSets An array of string arrays where each string array is a set of words in a phrase
 */
export const flattenNgrams = (wordSets: string[][]): string[] => {
  return wordSets.reduce((acc, currentVal) => {
    acc.push(currentVal.join(' '))
    return acc
  }, [])
}

export const generateNgrams = (words: string[], ngramCount: number) => {
  return words
    .slice(0, 1 - ngramCount)
    .map((_, i) => {
      return words.slice(i, i + ngramCount)
    })
}

export const generateAllGramSets = (words: string[]) => {
  const [bigrams, trigrams] = [
    generateNgrams(words, 2),
    generateNgrams(words, 3)
  ]

  const flatBigrams = flattenNgrams(bigrams)
  const flatTrigrams = flattenNgrams(trigrams)
  const sets = Array.from([words || [], flatBigrams, flatTrigrams].flat())
  return sets
}

/**
 * Cycle through the responses and use the tokenized parts of speech equivalent to determine if they are valid for
 * the wordcloud. It is important to note that these line up 1:1. Current check is the words being checked have to be
 * valid parts of speech in order for them to pass. (Noun, Proper noun, verb, or adjective)
 * @param responseWordSet responses being checked
 * @param tokenSet the tokenized versions of the responses which represent the part of speech
 */
export const filterGramsUsingPartsOfSpeech = (responseWordSet: string[], tokenSet: string[]) => {
  const validWordSet: string [] = []

  responseWordSet.forEach((word, index) => {
    if (!tokenSet[index]) return

    const tokenArray = tokenSet[index].split(' ')
    const leftToken = tokenArray[0]

    // If it's only one word, we only check if the word itself is valid
    if (tokenArray.length === 1) {
      if (validPosTokens.includes(leftToken)) {
        validWordSet.push(word)
      }
    } else {
      // If there are two or three words (current max), we check the first word and the last word for validity
      const rightToken = tokenArray[tokenArray.length - 1]
      if (validPosTokens.includes(leftToken) &&
        validPosTokens.includes(rightToken)) {
        validWordSet.push(word)
      }
    }
  })

  return validWordSet
}

/**
 * Generates sets of grams for 1-3 words
 * @param response Response which the grams are being generated for
 */
export const getNgrams = (response: Response): string[] => {
  if (!response.textAnswer) return []

  const textAnswer = cleanForLooseMatch(response.textAnswer)

  const allWordSets = generateAllGramSets(textAnswer.split(' '))
  return allWordSets
}

/**
 * Same as getNgrams but this time use the parts of speech tokens to filter out grams that don't pass the filter
 * @param response
 */
export const getFilteredNgrams = (response: Response): string[] => {
  if (!response.textAnswer) return []
  if (!response.spacyPos || !response.spacyLemma) return getNgrams(response)

  const responseWordSet = getNgrams(response)

  const textAnswer = cleanForLooseMatch(response.textAnswer)
  const cleanResponse = removeApostrophes(textAnswer.split(' '))

  const tokens = removeContractionTokens(cleanResponse, response.spacyPos.filter(word => word !== '.'))
  const tokenSet = generateAllGramSets(tokens)

  const validWordSets = filterGramsUsingPartsOfSpeech(responseWordSet, tokenSet)
  return validWordSets
}

/**
 * Create a dictionary of words with definitions of word lists that should be converted into it... for example
 * run: [run, ran, running] ... run, ran and running will be converted into "run" for our final count
 * @param responses The responses we are doing this for
 */
export const generateLemmaDictionary = (responses: Response[]) => {
  const lemmaDict: Record<string, string[]> = {}

  responses.forEach(response => {
    if (response.spacyLemma) {
      const textAnswer = cleanForLooseMatch(response.textAnswer)
      const cleanResponse = removeApostrophes(textAnswer.split(' '))
      const responseWordSet = getNgrams(response)

      const lemmaTokens = removeContractionTokens(cleanResponse, response.spacyLemma.filter(word => word !== '.'))
      const lemmaSets = generateAllGramSets(lemmaTokens)

      lemmaSets.forEach((lemma: string, index) => {
        // Use the real word if the lemma is invalid or if we run into a white list word
        let wordFromResponse = responseWordSet[index]
        // If the word is one of the custom ones we replace, do so before we use it
        if (customWordReplacements[wordFromResponse]) wordFromResponse = customWordReplacements[wordFromResponse]

        // Use real word if it isn't a custom replacer word and either the lemma is a pronoun or the word is whitelisted
        // Otherwise we use whatever the lemma version of the word is
        const useRealWord = !customWordReplacements[wordFromResponse] && (lemma?.includes('-PRON-') || whiteListWords.includes(wordFromResponse))

        const dictKey = useRealWord ? wordFromResponse : lemma
        lemmaDict[dictKey] ? lemmaDict[dictKey].push(wordFromResponse)
          : lemmaDict[dictKey] = [wordFromResponse]
      })
    }
  })

  return lemmaDict
}

/**
 * Use the lemma dict to combine counts words that have the same "root" word (word they are being turned into)
 * The word with highest frequency count of each group of words will be used as the new key
 * @param frequencyMap Count of all the words
 * @param lemmaDictionary Lemma definitions ... eg: "run": ["running", "run", "ran"], "football": ["footballs", "football"]
 * @return the new counts using lemma root as key. eg: "run": 125, "football": 22
 */
export const combineLemmatizedFrequencies = (frequencyMap: Record<string, number>, lemmaDictionary: Record<string, string[]>) => {
  const lemmatizedFrequencies: Record<string, number> = {}

  Object.values(lemmaDictionary).forEach(words => {
    // This is the word we display in the word cloud
    let wordForDisplay = ''
    let topWordCount = 0
    let totalWordCount = 0

    // Cycle through each word for the particular lemma entry, add them together, and figure out which
    // of the words to use as the key (one with highest count)
    words.forEach(word => {
      if (!frequencyMap[word]) return
      const currentWordCount = frequencyMap[word]
      if (!wordForDisplay || topWordCount < currentWordCount) {
        topWordCount = currentWordCount
        wordForDisplay = word
      }
      totalWordCount += frequencyMap[word]
    })

    // Now add it to new frequency dict
    if (wordForDisplay) {
      lemmatizedFrequencies[wordForDisplay] = totalWordCount
    }
  })

  return lemmatizedFrequencies
}
